import { currencyBRL } from '../../../core/helpers/string.helper';

export const SEAT_KINDS = [
  'standard',
  'wheelchair',
  'reduced_mobility',
  'obese',
  'companion',
  'premium'
];

export const SEAT_KIND_LABELS = {
  standard: 'Padrão',
  wheelchair: 'Cadeirante',
  reduced_mobility: 'Mobilidade reduzida',
  obese: 'Pessoa com obesidade',
  companion: 'Acompanhante',
  premium: 'Premium'
};

export const SEAT_KIND_DESCRIPTIONS = {
  standard: 'Assento comum.',
  wheelchair: 'Espaço para cadeira de rodas, reservado a pessoas cadeirantes.',
  reduced_mobility: 'Acesso facilitado, reservado a pessoas com mobilidade reduzida.',
  obese: 'Assento mais largo, reservado a pessoas com obesidade.',
  companion: 'Ao lado de um lugar acessível, reservado ao acompanhante.',
  premium: 'Assento especial (ex.: D-BOX, VIP) com acréscimo no valor.'
};

export const SEAT_KIND_ICONS = {
  standard: 'seat',
  wheelchair: 'seat-wheelchair',
  reduced_mobility: 'seat-reduced-mobility',
  obese: 'seat-obese',
  companion: 'seat-companion',
  premium: 'seat-premium'
};

export const ACCESSIBLE_SEAT_KINDS = ['wheelchair', 'reduced_mobility', 'obese', 'companion'];

export const SEAT_STATUS_LABELS = {
  available: 'disponível',
  reserved: 'indisponível (reservado)',
  sold: 'indisponível (vendido)',
  blocked: 'indisponível (bloqueado)',
  selected: 'selecionado'
};

export const DEFAULT_STAGE_LABEL = 'Tela';

export function rowLetter(index) {
  let value = index + 1;
  let label = '';
  while (value > 0) {
    const remainder = (value - 1) % 26;
    label = String.fromCharCode(65 + remainder) + label;
    value = Math.floor((value - 1) / 26);
  }
  return label;
}

export function toSeat(record) {
  const active = record.is_active !== false;
  return {
    id: record.id || null,
    rotulo: record.label,
    fileira: record.row_label,
    numero: record.number,
    linha: record.grid_row,
    coluna: record.grid_column,
    tipo: record.kind || 'standard',
    premiumRotulo: record.premium_label || '',
    acrescimoCentavos: record.surcharge_cents || 0,
    ativo: active,
    status: record.status || (active ? 'available' : 'blocked')
  };
}

export function toSeatMap(record) {
  const data = record || {};
  return {
    recursoId: data.resource_id || null,
    palco: data.stage_label || DEFAULT_STAGE_LABEL,
    linhas: data.rows || 0,
    colunas: data.columns || 0,
    assentos: (data.seats || []).map(toSeat)
  };
}

export function toSessionSeating(record) {
  const session = record.session || {};
  return {
    ...toSeatMap(record),
    sessao: {
      id: session.id,
      produtoId: session.product_id,
      inicio: session.starts_at,
      rotulo: session.label || ''
    },
    sala: record.resource ? { id: record.resource.id, nome: record.resource.name } : null
  };
}

export function seatMapPayload(map) {
  return {
    stageLabel: (map.palco || '').trim() || DEFAULT_STAGE_LABEL,
    rows: map.linhas,
    columns: map.colunas,
    seats: map.assentos.map((seat) => {
      const premium = seat.tipo === 'premium';
      return {
        label: seat.rotulo,
        rowLabel: seat.fileira,
        number: seat.numero,
        gridRow: seat.linha,
        gridColumn: seat.coluna,
        kind: seat.tipo,
        premiumLabel: premium && seat.premiumRotulo ? seat.premiumRotulo.trim() : null,
        surchargeCents: premium ? seat.acrescimoCentavos || 0 : 0,
        isActive: seat.ativo !== false
      };
    })
  };
}

export function seatGrid(map) {
  if (!map) return [];
  const byPosition = {};
  map.assentos.forEach((seat) => {
    byPosition[`${seat.linha}-${seat.coluna}`] = seat;
  });
  const rows = [];
  for (let linha = 1; linha <= map.linhas; linha += 1) {
    const celulas = [];
    for (let coluna = 1; coluna <= map.colunas; coluna += 1) {
      celulas.push({ linha, coluna, assento: byPosition[`${linha}-${coluna}`] || null });
    }
    const seated = celulas.find((cell) => cell.assento);
    rows.push({ linha, rotulo: seated ? seated.assento.fileira : '', celulas });
  }
  return rows;
}

export function seatKindsIn(map) {
  const present = new Set(((map && map.assentos) || []).map((seat) => seat.tipo));
  return SEAT_KINDS.filter((kind) => present.has(kind));
}

export function isAccessibleSeat(seat) {
  return ACCESSIBLE_SEAT_KINDS.includes(seat.tipo);
}

export function isSeatAvailable(seat) {
  return seat.status === 'available';
}

export function seatSurchargeCents(seat) {
  return seat.tipo === 'premium' ? seat.acrescimoCentavos || 0 : 0;
}

export function seatSurchargeLabel(seat) {
  const cents = seatSurchargeCents(seat);
  return cents ? `+${currencyBRL(cents / 100)}` : '';
}

export function seatKindLabel(seat) {
  const label = SEAT_KIND_LABELS[seat.tipo] || seat.tipo;
  return seat.tipo === 'premium' && seat.premiumRotulo ? `${label} ${seat.premiumRotulo}` : label;
}

export function seatAriaLabel(seat, selected) {
  const parts = [
    `Fileira ${seat.fileira}, assento ${seat.numero}`,
    SEAT_STATUS_LABELS[selected ? 'selected' : seat.status] || seat.status
  ];
  if (seat.tipo !== 'standard') {
    const kind = (SEAT_KIND_LABELS[seat.tipo] || seat.tipo).toLowerCase();
    parts.push(
      [kind, seat.tipo === 'premium' ? seat.premiumRotulo : '', seatSurchargeLabel(seat)]
        .filter(Boolean)
        .join(' ')
    );
  }
  return parts.join(', ');
}

export function surchargeTotalCents(seats) {
  return (seats || []).reduce((sum, seat) => sum + seatSurchargeCents(seat), 0);
}

export function accessibleSeatNotice(seat) {
  return `O assento ${seat.rotulo} (${seatKindLabel(seat).toLowerCase()}) é reservado a quem precisa. Confirme que a pessoa tem essa necessidade antes de vendê-lo.`;
}
